import { idlFactory as ethHelperFactory } from '../idls/ethHelper/ethHelper.did'
import type { _SERVICE as HelperActor, utxo } from '../idls/ethHelper/ethHelper.did'
import { Actor, ActorSubclass, fromHex, HttpAgent } from '@dfinity/agent'
import { Principal } from '@dfinity/principal'
import {
  ApproveErc20Params,
  BTCSendParams,
  SolSendParams,
  MintCkEthParams,
  SendErc20Params,
  SendEthParams,
  PersonalSignParams,
  EthCallContractParams,
  CustomDepositErc20Params,
  CustomApproveErc20Params,
  BTCSendOrdinalParams,
} from '../interfaces/walletActions'
import BitcoinClient from './BitcoinClient'
import { solanaClient } from './SolanaClient'
import { fetchEnv } from '../utils/fetchEnv'
import { logger } from '../logger'
import { getEthProvider } from '../utils/getEthProvider'
import { Chain } from '../interfaces/common'
import { getChainId } from '../utils/getChainId'

const HELPER_CANISTER_ID = 'ajx4k-liaaa-aaaal-ajqfq-cai'

class ChainFusionClient {
  private helperActor: ActorSubclass<HelperActor>

  constructor(agent: HttpAgent) {
    this.helperActor = Actor.createActor(ethHelperFactory, {
      canisterId: HELPER_CANISTER_ID,
      agent,
    }) as ActorSubclass<HelperActor>
  }

  async sendEth(params: SendEthParams) {
    const { address, amount, gasLimit, maxFeePerGas, maxPriorityFeePerGas, nonce, chain } = params

    const signedTransaction = await this.helperActor.send_eth(
      { Sign: null },
      address,
      BigInt(amount),
      BigInt(gasLimit || ''),
      BigInt(maxFeePerGas || ''),
      BigInt(maxPriorityFeePerGas || ''),
      BigInt(nonce),
      BigInt(getChainId(chain))
    )

    if ('Err' in signedTransaction) {
      return Promise.reject(signedTransaction.Err)
    }

    if ('Ok' in signedTransaction) {
      return this.sendEthTransaction(signedTransaction.Ok, chain)
    }
  }

  private async publishTransaction(transaction: string, chain: Chain) {
    try {
      return await this.sendEthTransaction(transaction, chain)
    } catch (e) {
      logger.debug('ChainFushion publishTransaction error', {
        originalError: e,
      })

      return Promise.reject((e as Error).message)
    }
  }

  private async sendEthTransaction(transaction: string, chain: Chain) {
    const provider = getEthProvider(chain)

    const hash = await provider.sendRawTransaction({
      serializedTransaction: transaction as `0x${string}`,
    })

    await provider.waitForTransactionReceipt({ hash, confirmations: 1 })

    return hash
  }

  async sendErc20(params: SendErc20Params) {
    const {
      amount,
      gasLimit,
      maxFeePerGas,
      maxPriorityFeePerGas,
      nonce,
      contractAddress,
      receiver,
      chain,
    } = params

    const signedTransaction = await this.helperActor.send_erc20(
      { Sign: null },
      BigInt(amount),
      BigInt(gasLimit),
      BigInt(maxFeePerGas),
      BigInt(maxPriorityFeePerGas),
      BigInt(nonce),
      BigInt(getChainId(chain)),
      contractAddress,
      receiver
    )

    if ('Err' in signedTransaction) {
      return Promise.reject(signedTransaction.Err)
    }

    if ('Ok' in signedTransaction) {
      return this.publishTransaction(signedTransaction.Ok, chain)
    }
  }

  async approveErc20(params: ApproveErc20Params) {
    const { amount, gasLimit, maxFeePerGas, maxPriorityFeePerGas, nonce, contractAddress, chain } =
      params

    const signedTransaction = await this.helperActor.approve_erc20(
      { Sign: null },
      BigInt(amount),
      BigInt(gasLimit),
      BigInt(maxFeePerGas),
      BigInt(maxPriorityFeePerGas),
      BigInt(nonce),
      BigInt(getChainId(chain)),
      contractAddress
    )

    if ('Err' in signedTransaction) {
      return Promise.reject(signedTransaction.Err)
    }

    if ('Ok' in signedTransaction) {
      return this.publishTransaction(signedTransaction.Ok, chain)
    }
  }

  async depositErc20(params: ApproveErc20Params) {
    const { amount, gasLimit, maxFeePerGas, maxPriorityFeePerGas, nonce, contractAddress, chain } =
      params

    const signedTransaction = await this.helperActor.deposit_erc20(
      { Sign: null },
      BigInt(amount),
      BigInt(gasLimit),
      BigInt(maxFeePerGas),
      BigInt(maxPriorityFeePerGas),
      BigInt(nonce),
      BigInt(getChainId(chain)),
      contractAddress
    )

    if ('Err' in signedTransaction) {
      return Promise.reject(signedTransaction.Err)
    }

    if ('Ok' in signedTransaction) {
      return this.publishTransaction(signedTransaction.Ok, chain)
    }
  }

  async mintCkEth(params: MintCkEthParams) {
    const response = await this.helperActor.mint_cketh(
      { Sign: null },
      BigInt(params.amount),
      BigInt(params.gasLimit || ''),
      BigInt(params.maxFeePerGas || ''),
      BigInt(params.maxPriorityFeePerGas || ''),
      BigInt(params.nonce),
      BigInt(getChainId(params.chain))
    )

    if ('Ok' in response) {
      return this.publishTransaction(response.Ok, params.chain)
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async personalSign(params: PersonalSignParams) {
    const response = await this.helperActor.personal_sign(params.message)

    if ('Ok' in response) {
      return response.Ok
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async ethCallContract(params: EthCallContractParams) {
    const response = await this.helperActor.eth_call_contract(
      { Sign: null },
      BigInt(params.value),
      BigInt(params.gasLimit),
      BigInt(params.maxFeePerGas),
      BigInt(params.maxPriorityFeePerGas),
      BigInt(params.nonce),
      BigInt(getChainId(params.chain)),
      params.contractAddress,
      params.data
    )

    if ('Ok' in response) {
      return this.publishTransaction(response.Ok, params.chain)
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async customDepositErc20(params: CustomDepositErc20Params) {
    const response = await this.helperActor.custom_deposit_erc20(
      { Sign: null },
      BigInt(params.value),
      BigInt(params.gasLimit),
      BigInt(params.maxFeePerGas),
      BigInt(params.maxPriorityFeePerGas),
      BigInt(params.nonce),
      BigInt(getChainId(params.chain)),
      params.contractAddress
    )

    if ('Ok' in response) {
      return this.publishTransaction(response.Ok, params.chain)
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async customApproveErc20(params: CustomApproveErc20Params) {
    const response = await this.helperActor.custom_approve_erc20(
      { Sign: null },
      BigInt(params.value),
      BigInt(params.gasLimit),
      BigInt(params.maxFeePerGas),
      BigInt(params.maxPriorityFeePerGas),
      BigInt(params.nonce),
      BigInt(getChainId(params.chain)),
      params.contractAddress,
      params.spender
    )

    if ('Ok' in response) {
      return this.publishTransaction(response.Ok, params.chain)
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async getBtcAddress(principal: Principal) {
    const response = await this.helperActor.btc_p2tr_raw_key_address(
      principal,
      fetchEnv('BITCOIN_CHAIN') === 'testnet'
        ? { Testnet: null }
        : {
            Mainnet: null,
          }
    )

    if ('Ok' in response) {
      return response.Ok
    } else {
      throw response.Err
    }
  }

  async sendBTC({ amount, to, fee, utxos }: BTCSendParams) {
    const mappedUtxo: utxo[] = utxos.map((u) => ({
      height: u.mintHeight,
      value: BigInt(u.value),
      outpoint: {
        vout: u.mintIndex,
        txid: Array.from(new Uint8Array(fromHex(u.mintTxid))).reverse(),
      },
    }))

    const response = await this.helperActor.send_btc(
      { Sign: null },
      to,
      BigInt(amount),
      BigInt(fee),
      fetchEnv('BITCOIN_CHAIN') === 'testnet' ? { Testnet: null } : { Mainnet: null },
      mappedUtxo
    )

    if ('Ok' in response) {
      const { txid } = await BitcoinClient.publishTransaction(response.Ok)
      return txid
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  async sendOrdinal({ to, fee, ordinalUtxo, feeUtxo }: BTCSendOrdinalParams) {
    const mappedOrdinalUtxo: utxo = {
      height: ordinalUtxo.mintHeight,
      value: BigInt(ordinalUtxo.value),
      outpoint: {
        vout: ordinalUtxo.mintIndex,
        txid: Array.from(new Uint8Array(fromHex(ordinalUtxo.mintTxid))).reverse(),
      },
    }

    const mappedFeeUtxo: utxo = {
      height: feeUtxo.mintHeight,
      value: BigInt(feeUtxo.value),
      outpoint: {
        vout: feeUtxo.mintIndex,
        txid: Array.from(new Uint8Array(fromHex(feeUtxo.mintTxid))).reverse(),
      },
    }

    const response = await this.helperActor.send_ordinal(
      { Sign: null },
      to,
      BigInt(fee),
      fetchEnv('BITCOIN_CHAIN') === 'testnet' ? { Testnet: null } : { Mainnet: null },
      mappedOrdinalUtxo,
      mappedFeeUtxo
    )

    if ('Ok' in response) {
      const { txid } = await BitcoinClient.publishTransaction(response.Ok)
      return txid
    }

    if ('Err' in response) {
      return Promise.reject(response.Err)
    }
  }

  getEthAddress(principal: Principal) {
    return this.helperActor.eth_address([principal])
  }

  async getSolAddress(principal: Principal) {
    const response = await this.helperActor.solana_address(principal)

    if ('Ok' in response) {
      return response.Ok
    } else {
      throw response.Err
    }
  }

  sendSol(solSendParams: SolSendParams) {
    return solanaClient.sendSol(solSendParams, this.helperActor)
  }
}

export default ChainFusionClient
